import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProcurementNav from '../components/ProcurementNav';
import StatCard from '../components/StatCard';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api, { inventoryApi } from '../services/api';
import InvoiceScanReceiveModal from '../components/InvoiceScanReceiveModal';
import {
  Plus,
  ScanLine,
  Search,
  AlertTriangle,
  Package,
  ArrowDown,
  ArrowUp,
  History,
  X,
  Edit2,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Sparkles,
  UtensilsCrossed,
  Check,
  DollarSign,
  Truck,
  ClipboardList,
  Copy,
} from 'lucide-react';
import HelpButton from '../components/HelpButton';
import clsx from 'clsx';

interface InventoryItem {
  id: string;
  name: string;
  sku?: string;
  unit: string;
  currentQuantity: number;
  parLevel: number;
  unitCost: number;
  lastCountedAt?: string;
  category?: string;
  supplier?: string;
  supplierContact?: string;
  // Weeks of cover at the trailing-4-week wholesale pace; null = no recent
  // wholesale depletion matched to this item (the UI shows a dash).
  weeksOfCover?: number | null;
  weeklyDepletion?: number | null;
}

interface DuplicateGroupItem {
  id: string;
  name: string;
  unit: string;
  unitCostCents: number;
  currentQuantity: number;
  createdAt: string;
  references: { transactions: number; recipeIngredients: number; purchaseOrderItems: number };
}

interface DuplicateGroup {
  key: string;
  likelySameSku: boolean;
  items: DuplicateGroupItem[];
}

const defaultFormData = {
  name: '',
  sku: '',
  unit: 'units',
  currentQuantity: 0,
  parLevel: 10,
  // Held as the raw typed string while editing — formatting-on-keystroke
  // made the field fight the user and save mangled numbers.
  unitCost: '' as string | number,
  category: 'Produce',
  supplier: '',
  supplierContact: '',
};

// Restaurant inventory categories
const INVENTORY_CATEGORIES = [
  'Produce',
  'Meat',
  'Poultry',
  'Seafood',
  'Dairy',
  'Dry Goods',
  'Frozen',
  'Bakery',
  'Beverages',
  'Alcohol',
  'Spices & Seasonings',
  'Oils & Vinegars',
  'Paper Goods',
  'Cleaning',
  'Smallwares',
  'Miscellaneous',
];

export default function InventoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'low'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [formData, setFormData] = useState(defaultFormData);
  const [adjustData, setAdjustData] = useState({ quantity: 0, reason: '' });
  const [formError, setFormError] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(true);

  // Fetch inventory
  const { data: inventoryData, isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: async () => {
      const response = await inventoryApi.getItems();
      const result = response.data?.data || response.data;
      return Array.isArray(result) ? result : [];
    },
  });

  // Fetch suggestions from menu analysis
  const { data: suggestionsData } = useQuery({
    queryKey: ['inventory-suggestions'],
    queryFn: async () => {
      const response = await inventoryApi.getSuggestions();
      return response.data?.data || response.data || null;
    },
  });

  // Count containers (R365-style): how each item is physically counted.
  const { data: containersMap } = useQuery({
    queryKey: ['count-containers'],
    queryFn: async () => (await api.get('/v1/inventory/count-containers')).data?.data || {},
  });

  // Duplicate candidates — the report comes first; merges are one human
  // click per pair, and all history moves to the record that stays.
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [survivorPick, setSurvivorPick] = useState<Record<string, string>>({});
  const { data: dupData, isLoading: dupsLoading } = useQuery({
    queryKey: ['inventory-duplicates'],
    queryFn: async () => (await inventoryApi.getDuplicates()).data?.data || { groups: [] },
    enabled: showDuplicates,
  });
  const dupGroups: DuplicateGroup[] = dupData?.groups || [];
  const mergeMutation = useMutation({
    mutationFn: ({ survivorId, mergedId }: { survivorId: string; mergedId: string }) =>
      inventoryApi.mergeItems(survivorId, mergedId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-duplicates'] });
    },
  });
  const [countRows, setCountRows] = useState<Record<string, number>>({});
  const [looseQty, setLooseQty] = useState(0);

  // A full count session: walk the shelves in category order, enter containers
  // and loose units per item. Entries stay raw strings while typing — parsed
  // on compute, so the fields never fight the counter.
  const [countMode, setCountMode] = useState(false);
  const [countSession, setCountSession] = useState<Record<string, { conts: Record<string, string>; loose: string }>>({});
  const [countSummary, setCountSummary] = useState('');
  const [applyingCount, setApplyingCount] = useState(false);

  // Photo count session: each shelf photo adds estimated rows; a human
  // confirms, then counts apply through the normal count path.
  const [photoRows, setPhotoRows] = useState<Array<{ itemId: string; name: string; unit: string; counted: number; basis: string | null; confidence: string; expected: number; pendingDelivery: number }>>([]);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoNote, setPhotoNote] = useState('');
  const [applyingPhoto, setApplyingPhoto] = useState(false);
  const handlePhotoCount = async (file: File) => {
    setPhotoBusy(true); setPhotoNote('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/v1/inventory/photo-count', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const d = res.data?.data || {};
      const rows = d.rows || [];
      setPhotoRows((prev) => {
        const byId = new Map(prev.map((r) => [r.itemId, r]));
        for (const r of rows) {
          const ex = byId.get(r.itemId);
          if (ex) byId.set(r.itemId, { ...ex, counted: Math.round((ex.counted + r.counted) * 100) / 100, basis: [ex.basis, r.basis].filter(Boolean).join(' + ') });
          else byId.set(r.itemId, r);
        }
        return [...byId.values()];
      });
      setPhotoNote(rows.length ? `${rows.length} item${rows.length === 1 ? '' : 's'} read from this photo${d.sceneNotes ? ` · ${d.sceneNotes}` : ''}` : (d.sceneNotes || "Nothing countable recognized — try a closer shot."));
    } catch (e: any) {
      setPhotoNote(e?.response?.data?.error?.message || 'The photo could not be read.');
    } finally { setPhotoBusy(false); }
  };
  const applyPhotoCounts = async () => {
    setApplyingPhoto(true);
    try {
      for (const r of photoRows) {
        const delta = Math.round((r.counted - r.expected) * 1000) / 1000;
        if (Math.abs(delta) < 0.01) continue;
        await api.post(`/v1/inventory/${r.itemId}/transactions`, {
          type: 'adjustment', quantity: delta, reason: 'Inventory count',
          notes: `Photo count: ${r.counted} ${r.unit}${r.basis ? ` (${r.basis})` : ''}`,
        });
      }
      setPhotoRows([]); setPhotoNote('Counts applied — on hand snapped to what the camera saw.');
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    } finally { setApplyingPhoto(false); }
  };

  // "Who has X to spare?" — the cross-store matrix (multi-store accounts)
  const [acrossQ, setAcrossQ] = useState('');
  const { data: across } = useQuery({
    queryKey: ['across-stores', acrossQ],
    queryFn: async () => (await api.get(`/v1/inventory/across-stores?q=${encodeURIComponent(acrossQ)}`)).data?.data,
    staleTime: 30_000,
    // Keep the previous result on screen while the next query loads. Without
    // this the card unmounts on every keystroke (data briefly undefined →
    // the stores>1 guard fails), so the search input loses focus after one
    // character and the search feels broken.
    placeholderData: (prev) => prev,
  });

  // Items the camera saw during counts that aren't on the list — manager
  // decides; the count flow itself never adds items.
  const { data: itemReviews } = useQuery({
    queryKey: ['item-reviews'],
    queryFn: async () => (await api.get('/v1/inventory/item-reviews')).data?.data || { pending: [] },
  });
  const resolveReview = useMutation({
    mutationFn: async (v: { name: string; action: 'add' | 'dismiss'; unit?: string }) =>
      api.post('/v1/inventory/item-reviews/resolve', v),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-reviews'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });

  // Pending deliveries — open POs that receiving still expects.
  const { data: pendingDeliveries } = useQuery({
    queryKey: ['pending-deliveries'],
    queryFn: async () => (await api.get('/v1/inventory/pending-deliveries')).data?.data || [],
  });

  // Sales-driven depletion (Toast): when live, on-hand maintains itself.
  const { data: toastStatus } = useQuery({
    queryKey: ['toast-status-inventory'],
    queryFn: async () => (await api.get('/v1/integrations/toast/status')).data?.data,
    staleTime: 60_000,
  });
  const depletion = toastStatus?.depletion;
  const usedToday: Record<string, number> = depletion?.usedToday || {};

  // Use real data only - no mock fallbacks
  const items = Array.isArray(inventoryData) ? inventoryData : [];
  const suggestions = suggestionsData || { recipeIssues: [], suggestedIngredients: [], summary: {} };

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => inventoryApi.createItem({
      name: data.name,
      sku: data.sku || undefined,
      unit: data.unit,
      category: data.category || 'Miscellaneous',
      currentQuantity: data.currentQuantity,
      parLevel: data.parLevel,
      unitCost: parseFloat(String(data.unitCost)) || 0,
      supplier: data.supplier || undefined,
      supplierContact: data.supplierContact || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowAddModal(false);
      setFormData(defaultFormData);
      setFormError('');
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to create item');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      inventoryApi.updateItem(id, { ...data, unitCost: parseFloat(String(data.unitCost ?? 0)) || 0 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowAddModal(false);
      setSelectedItem(null);
      setFormData(defaultFormData);
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to update item');
    },
  });

  // Adjust quantity mutation
  const adjustMutation = useMutation({
    mutationFn: ({ id, quantity, reason }: { id: string; quantity: number; reason: string }) =>
      inventoryApi.adjustQuantity(id, quantity, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setShowAdjustModal(false);
      setSelectedItem(null);
      setAdjustData({ quantity: 0, reason: '' });
    },
    onError: (error: any) => {
      setFormError(error.response?.data?.error?.message || 'Failed to adjust quantity');
    },
  });

  // Only count as low stock if parLevel is set and quantity is below it
  // Convert to numbers since database may return strings for decimal columns
  const lowStockItems = items.filter((item: InventoryItem) => {
    const qty = Number(item.currentQuantity) || 0;
    const par = Number(item.parLevel) || 0;
    return par > 0 && qty < par;
  });

  // Get unique categories from actual items for the filter and form dropdowns
  const itemCategories = [...new Set(items.map((item: InventoryItem) => item.category).filter(Boolean))].sort() as string[];

  const filteredItems = items.filter((item: InventoryItem) => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const qty = Number(item.currentQuantity) || 0;
    const par = Number(item.parLevel) || 0;
    const matchesStockFilter = filter === 'all' || (par > 0 && qty < par);
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesStockFilter && matchesCategory;
  });

  const formatCurrency = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const getStockStatus = (item: InventoryItem) => {
    const qty = Number(item.currentQuantity) || 0;
    const par = Number(item.parLevel) || 0;
    if (par === 0) return 'good'; // No par level set
    const percentage = (qty / par) * 100;
    if (percentage <= 50) return 'critical';
    if (percentage < 100) return 'low';
    return 'good';
  };

  const openEditModal = (item: InventoryItem) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      sku: item.sku || '',
      unit: item.unit || 'units',
      currentQuantity: item.currentQuantity,
      parLevel: item.parLevel || 10,
      unitCost: (item.unitCost ?? 0).toFixed(2),
      category: item.category || 'Miscellaneous',
      supplier: item.supplier || '',
      supplierContact: item.supplierContact || '',
    });
    setShowAddModal(true);
  };

  const [showLowStockDetail, setShowLowStockDetail] = useState(false);

  const openAdjustModal = (item: InventoryItem) => {
    setCountRows({}); setLooseQty(0);
    setSelectedItem(item);
    setAdjustData({ quantity: 0, reason: '' });
    setShowAdjustModal(true);
  };

  // One conversion for every count surface — containers × their size + loose
  // stock units. The adjust modal and the count session both go through here.
  const countedFrom = (defs: Array<{ label: string; qty: number }>, conts: Record<string, number>, loose: number) =>
    Math.round((defs.reduce((sum, c) => sum + (conts[c.label] || 0) * c.qty, 0) + (loose || 0)) * 1000) / 1000;

  const sessionEntry = (item: InventoryItem) => {
    const defs = (((containersMap || {}) as Record<string, Array<{ label: string; qty: number }>>)[item.id]) || [];
    const e = countSession[item.id];
    const entered = !!e && (Object.values(e.conts).some((v) => v.trim() !== '') || e.loose.trim() !== '');
    const conts: Record<string, number> = {};
    if (e) for (const [label, v] of Object.entries(e.conts)) conts[label] = parseFloat(v) || 0;
    const counted = countedFrom(defs, conts, e ? parseFloat(e.loose) || 0 : 0);
    const expected = Number(item.currentQuantity) || 0;
    const pending = Number((item as any).onOrderQuantity) || 0;
    return { defs, entered, counted, expected, pending, variance: Math.round((counted - expected) * 1000) / 1000 };
  };

  // "Meaningfully off" = more than 10% of expected (any gap when expected is
  // 0) — the same tolerance the photo recap uses. It only colors a row;
  // counts always apply as entered.
  const meaningfulGap = (variance: number, expected: number) =>
    expected > 0 ? Math.abs(variance) / expected > 0.1 : Math.abs(variance) >= 0.01;

  const setSessionValue = (itemId: string, field: 'loose' | { container: string }, value: string) => {
    setCountSession((prev) => {
      const e = prev[itemId] || { conts: {}, loose: '' };
      return { ...prev, [itemId]: field === 'loose' ? { ...e, loose: value } : { ...e, conts: { ...e.conts, [field.container]: value } } };
    });
  };

  const applyCountSession = async () => {
    setApplyingCount(true);
    try {
      const countedItems = (items as InventoryItem[]).filter((it) => sessionEntry(it).entered);
      let adjusted = 0;
      let largest: { name: string; unit: string; variance: number } | null = null;
      for (const it of countedItems) {
        const { defs, counted, variance } = sessionEntry(it);
        if (largest === null || Math.abs(variance) > Math.abs(largest.variance)) largest = { name: it.name, unit: it.unit, variance };
        if (Math.abs(variance) < 0.01) continue; // shelf matches — nothing to write
        const e = countSession[it.id] || { conts: {}, loose: '' };
        const parts = defs.filter((c) => (parseFloat(e.conts[c.label] || '') || 0) > 0).map((c) => `${parseFloat(e.conts[c.label])} × ${c.label}`);
        const loose = parseFloat(e.loose || '') || 0;
        if (loose > 0) parts.push(`${loose} loose ${it.unit}`);
        await api.post(`/v1/inventory/${it.id}/transactions`, {
          type: 'adjustment', quantity: variance, reason: 'Inventory count',
          notes: `Count: ${counted} ${it.unit}${parts.length ? ` (${parts.join(' + ')})` : ''}`,
        });
        adjusted++;
      }
      const gap = largest && Math.abs(largest.variance) >= 0.01
        ? `, largest variance ${largest.name} ${largest.variance > 0 ? '+' : '−'}${Math.abs(largest.variance)} ${largest.unit}.`
        : ' — the shelf matches what the system expected.';
      setCountSummary(`${countedItems.length} item${countedItems.length === 1 ? '' : 's'} counted, ${adjusted} adjusted${gap}`);
      setCountSession({});
      setCountMode(false);
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    } finally { setApplyingCount(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 pb-safe">
      <ProcurementNav />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Inventory</h1>
            <p className="text-sm md:text-base text-slate-500">Track and manage your stock levels</p>
          </div>
          <HelpButton featureId="inventory" />
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            onClick={() => setShowDuplicates((v) => !v)}
            className="btn btn-secondary flex items-center justify-center gap-2 flex-1 sm:flex-none py-3 sm:py-2"
          >
            <Copy className="w-5 h-5 sm:w-4 sm:h-4" />
            Duplicates
          </button>
          <button
            onClick={() => { setCountMode((v) => !v); setCountSummary(''); }}
            className="btn btn-secondary flex items-center justify-center gap-2 flex-1 sm:flex-none py-3 sm:py-2"
          >
            <ClipboardList className="w-5 h-5 sm:w-4 sm:h-4" />
            {countMode ? 'Close count' : 'Start a count'}
          </button>
          <button
            onClick={() => setShowScanModal(true)}
            className="btn btn-secondary flex items-center justify-center gap-2 flex-1 sm:flex-none py-3 sm:py-2"
          >
            <ScanLine className="w-5 h-5 sm:w-4 sm:h-4" />
            Scan invoice
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary flex items-center justify-center gap-2 flex-1 sm:flex-none py-3 sm:py-2"
          >
            <Plus className="w-5 h-5 sm:w-4 sm:h-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Stats — kit pattern: the numbers lead, right under the header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard title="Total items" value={items.length} icon={<Package className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />} iconBg="bg-primary-50" />
        <StatCard
          title="Low stock"
          value={lowStockItems.length}
          sub={(() => { const c = lowStockItems.filter((i: InventoryItem) => i.currentQuantity <= (Number((i as any).reorderPoint) || 0) * 0.5).length; return c > 0 ? `${c} critical` : undefined; })()}
          subTone="danger"
          icon={<AlertTriangle className="w-4 h-4 md:w-5 md:h-5 text-warning-600" />} iconBg="bg-warning-50"
        />
        <StatCard
          title="On order"
          value={items.filter((i: any) => (Number(i.onOrderQuantity) || 0) > 0).length}
          icon={<Truck className="w-4 h-4 md:w-5 md:h-5 text-primary-600" />} iconBg="bg-primary-50"
          onClick={() => navigate('/receiving')}
        />
        <StatCard
          title="Stock value"
          value={formatCurrency(Math.round(items.reduce((sum: number, i: InventoryItem) => sum + (i.currentQuantity * (i.unitCost || 0)), 0) * 100))}
          icon={<DollarSign className="w-4 h-4 md:w-5 md:h-5 text-success-600" />} iconBg="bg-success-50"
        />
      </div>

      {/* The quiet recap after a count is applied */}
      {countSummary && (
        <div className="card p-4 bg-emerald-50/60 border-emerald-200 flex items-start justify-between gap-3">
          <p className="text-sm text-emerald-900">{countSummary}</p>
          <button className="text-emerald-700/60 hover:text-emerald-900" onClick={() => setCountSummary('')}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Duplicates review — the list comes first; nothing merges without a click */}
      {showDuplicates && (
        <div className="card p-5 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-slate-900">Duplicate items</p>
              <p className="text-xs text-slate-500 max-w-2xl mt-0.5">
                Likely double-entries of the same item — old and new records from an import, variant
                spellings, unit variants. Merging keeps all history: stock movements, recipes, and
                order lines move onto the record you keep, and the quantities add together. Nothing
                merges without your click, one pair at a time.
              </p>
            </div>
            <button className="text-slate-400 hover:text-slate-600" onClick={() => setShowDuplicates(false)}><X className="w-4 h-4" /></button>
          </div>
          {mergeMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {(mergeMutation.error as any)?.response?.data?.error?.message || "That merge didn't go through — please try again."}
            </div>
          )}
          {dupsLoading ? (
            <p className="text-sm text-slate-500">Looking for duplicates…</p>
          ) : dupGroups.length === 0 ? (
            <p className="text-sm text-slate-500">No duplicate candidates found — every item name stands on its own.</p>
          ) : (
            <div className="space-y-3">
              {dupGroups.map((group) => {
                const survivorId = survivorPick[group.key] || group.items[0]?.id;
                return (
                  <div key={group.key} className="border border-slate-200 rounded-lg p-4 space-y-2">
                    <span className={clsx(
                      'inline-block text-xs font-medium px-2 py-0.5 rounded-full',
                      group.likelySameSku ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    )}>
                      {group.likelySameSku ? 'Likely the same item' : 'Pack-size variants — check before merging'}
                    </span>
                    {group.items.map((it) => {
                      const isSurvivor = it.id === survivorId;
                      const refs = it.references;
                      const refSummary = `${refs.transactions} movement${refs.transactions === 1 ? '' : 's'} · ${refs.recipeIngredients} recipe line${refs.recipeIngredients === 1 ? '' : 's'} · ${refs.purchaseOrderItems} order line${refs.purchaseOrderItems === 1 ? '' : 's'}`;
                      return (
                        <div key={it.id} className={clsx('flex items-center justify-between gap-3 p-2 rounded-lg', isSurvivor && 'bg-slate-50')}>
                          <label className="flex items-center gap-3 cursor-pointer min-w-0">
                            <input
                              type="radio"
                              name={`keep-${group.key}`}
                              checked={isSurvivor}
                              onChange={() => setSurvivorPick((p) => ({ ...p, [group.key]: it.id }))}
                              className="accent-primary-600"
                            />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{it.name}</p>
                              <p className="text-xs text-slate-500">
                                {it.currentQuantity} {it.unit} on hand · {formatCurrency(it.unitCostCents)}/{it.unit} · added {new Date(it.createdAt).toLocaleDateString()} · {refSummary}
                              </p>
                            </div>
                          </label>
                          {isSurvivor ? (
                            <span className="text-xs text-slate-400 shrink-0">keeping this one</span>
                          ) : (
                            <button
                              onClick={() => mergeMutation.mutate({ survivorId, mergedId: it.id })}
                              disabled={mergeMutation.isPending}
                              className="btn btn-sm btn-secondary shrink-0"
                            >
                              {mergeMutation.isPending && (mergeMutation.variables as any)?.mergedId === it.id ? 'Merging…' : 'Merge into →'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Count session — the walk-through sheet, in shelf order */}
      {countMode && (
        <div className="card p-5 space-y-4">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="font-semibold text-slate-900">Count the shelves</p>
              <p className="text-xs text-slate-500 max-w-2xl mt-0.5">
                {items.some((i: InventoryItem) => i.lastCountedAt)
                  ? 'Top to bottom, in shelf order. Enter containers and loose units — the sheet does the stock-unit math. Expected is what the system believes is on hand right now.'
                  : 'Counts verify what the system expects — stock moves with sales and receiving; you count to check it, not to build it.'}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => { setCountSession({}); setCountMode(false); }} className="btn btn-sm btn-ghost">Discard</button>
              <button
                onClick={applyCountSession}
                disabled={applyingCount || !(items as InventoryItem[]).some((it) => sessionEntry(it).entered)}
                className="btn btn-sm btn-primary"
              >
                {applyingCount ? 'Applying…' : (() => { const n = (items as InventoryItem[]).filter((it) => sessionEntry(it).entered).length; return n > 0 ? `Apply ${n} count${n === 1 ? '' : 's'}` : 'Apply counts'; })()}
              </button>
            </div>
          </div>

          {items.length === 0 ? (
            <p className="text-sm text-slate-500">No items yet — add your first item and it becomes a line on this sheet.</p>
          ) : (
            (() => {
              const order = new Map(INVENTORY_CATEGORIES.map((c, i) => [c, i]));
              const byCat = new Map<string, InventoryItem[]>();
              for (const it of items as InventoryItem[]) {
                const cat = it.category || 'Miscellaneous';
                if (!byCat.has(cat)) byCat.set(cat, []);
                byCat.get(cat)!.push(it);
              }
              const groups = [...byCat.entries()].sort((a, b) => (order.get(a[0]) ?? 99) - (order.get(b[0]) ?? 99) || a[0].localeCompare(b[0]));
              return groups.map(([cat, group]) => (
                <div key={cat}>
                  <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">{cat}</p>
                  <div className="divide-y divide-slate-100">
                    {group.map((item) => {
                      const { defs, entered, counted, expected, pending, variance } = sessionEntry(item);
                      const e = countSession[item.id];
                      return (
                        <div key={item.id} className="py-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <p className="font-medium text-slate-900">{item.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5 tabular-nums">
                              Expected {expected} {item.unit}
                              {pending > 0 && <span className="text-amber-600"> · +{pending} {item.unit} on the road</span>}
                            </p>
                          </div>
                          <div className="space-y-1">
                            {defs.map((c) => (
                              <div key={c.label} className="flex items-center justify-between gap-2">
                                <span className="text-xs text-slate-500">{c.label}</span>
                                <input type="number" min="0" step="1" inputMode="decimal" className="input input-sm w-20 text-right" placeholder="0"
                                  value={e?.conts[c.label] ?? ''}
                                  onChange={(ev) => setSessionValue(item.id, { container: c.label }, ev.target.value)} />
                              </div>
                            ))}
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs text-slate-500">{defs.length ? `loose ${item.unit}` : `total ${item.unit}`}</span>
                              <input type="number" min="0" step="0.1" inputMode="decimal" className="input input-sm w-20 text-right" placeholder="0"
                                value={e?.loose ?? ''}
                                onChange={(ev) => setSessionValue(item.id, 'loose', ev.target.value)} />
                            </div>
                          </div>
                          <div className="text-sm md:text-right space-y-0.5 tabular-nums">
                            {entered ? (
                              <>
                                <p className="font-semibold text-slate-900">Counted {Math.round(counted * 100) / 100} {item.unit}</p>
                                <p className={Math.abs(variance) < 0.01 ? 'text-slate-400' : meaningfulGap(variance, expected) ? 'text-amber-600 font-medium' : 'text-slate-500'}>
                                  {Math.abs(variance) < 0.01 ? 'matches expected' : `${Math.abs(variance)} ${item.unit} ${variance > 0 ? 'over' : 'under'} expected`}
                                </p>
                              </>
                            ) : (
                              <p className="text-slate-300">not counted yet</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ));
            })()
          )}
          {items.length > 0 && (
            <p className="text-xs text-slate-400">Expected comes from the system's running on-hand — sales deplete it, receiving adds to it; a delivery still on the road is noted beside it. Applying writes an adjustment only where the shelf disagrees; rows that match are left as they are.</p>
          )}
        </div>
      )}

      {/* Across stores — who has what, one glance */}
      {(across?.stores?.length || 0) > 1 && (
        <div className="card p-4 md:p-5">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
            <div>
              <p className="font-semibold text-slate-900">Across your stores</p>
              <p className="text-xs text-slate-500">Who has what to spare — ask in chat to move it ("send 10 lb of tomatoes to Tribeca").</p>
            </div>
            <input className="input input-sm w-56" placeholder="Search an item… e.g. tomatoes" value={acrossQ} onChange={(e) => setAcrossQ(e.target.value)} />
          </div>
          {(across.rows || []).length === 0 ? (
            <p className="text-sm text-slate-400 py-2">{acrossQ ? `Nothing matching "${acrossQ}" at any store.` : 'Search an item to compare it across stores.'}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr>
                  <th className="table-header">Item</th>
                  {across.stores.map((st: any) => <th key={st.id} className={clsx('table-header text-right', st.id === across.hereId && 'text-primary-600')}>{st.name}</th>)}
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {(acrossQ ? across.rows : across.rows.slice(0, 8)).map((r: any) => {
                    const max = Math.max(...across.stores.map((st: any) => r.stores[st.id]?.qty ?? 0));
                    return (
                      <tr key={r.name}>
                        <td className="table-cell font-medium">{r.name} <span className="text-xs text-slate-400">{r.unit}</span></td>
                        {across.stores.map((st: any) => {
                          const cell = r.stores[st.id];
                          return <td key={st.id} className={clsx('table-cell text-right tabular-nums', cell && cell.qty === max && max > 0 ? 'font-bold text-slate-900' : cell && cell.qty === 0 ? 'text-slate-300' : 'text-slate-600')}>{cell ? cell.qty : '—'}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="text-[11px] text-slate-400 mt-2">Bold = most on hand. A 0 that's never been counted means "unknown", not "empty" — one photo count anchors it.</p>
            </div>
          )}
        </div>
      )}

      {/* Count flagged these for a manager's call */}
      {((itemReviews?.pending || []) as any[]).length > 0 && (
        <div className="card p-4 bg-sky-50/60 border-sky-200">
          <p className="font-medium text-sky-900 mb-1">Seen on the shelf, not on the list</p>
          <p className="text-xs text-sky-800/70 mb-2">Photo counts spotted these. Add them to inventory or dismiss — staff counts never add items on their own.</p>
          <div className="space-y-1.5">
            {(itemReviews.pending as any[]).map((u: any) => (
              <div key={u.name} className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-sm text-slate-800">
                  {u.name}
                  {u.estimate ? <span className="text-slate-400"> · saw ~{u.estimate}</span> : null}
                  <span className="text-slate-400"> · {new Date(u.seenAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                </span>
                <span className="flex items-center gap-2">
                  <button className="text-xs font-medium text-sky-700 hover:text-sky-900"
                    onClick={() => { const unit = window.prompt(`Stock unit for ${u.name}?`, 'each'); if (unit) resolveReview.mutate({ name: u.name, action: 'add', unit }); }}>
                    Add to inventory
                  </button>
                  <button className="text-xs text-slate-400 hover:text-slate-600" onClick={() => resolveReview.mutate({ name: u.name, action: 'dismiss' })}>Dismiss</button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Low Stock Alert */}
      {lowStockItems.length > 0 && (
        <div className="card p-4 bg-warning-50 border-warning-200">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-warning-600 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-warning-800">
                {lowStockItems.length} item{lowStockItems.length === 1 ? '' : 's'} below reorder point — Ordering already has them suggested.
                <button className="ml-2 text-sm underline text-warning-700" onClick={() => setShowLowStockDetail((v: boolean) => !v)}>
                  {showLowStockDetail ? 'hide' : 'see which'}
                </button>
              </p>
              <div className={showLowStockDetail ? 'mt-2 space-y-2' : 'hidden'}>
                {/* Group by supplier */}
                {Object.entries(
                  lowStockItems.reduce((acc: Record<string, InventoryItem[]>, item: InventoryItem) => {
                    const supplier = item.supplier || 'No supplier set';
                    if (!acc[supplier]) acc[supplier] = [];
                    acc[supplier].push(item);
                    return acc;
                  }, {})
                ).map(([supplier, items]) => (
                  <div key={supplier} className="text-sm">
                    <span className="font-medium text-warning-700">{supplier}:</span>{' '}
                    <span className="text-warning-600">
                      {(items as InventoryItem[]).map((i: InventoryItem) => i.name).join(', ')}
                    </span>
                    {supplier !== 'No supplier set' && (items as InventoryItem[])[0]?.supplierContact && (
                      <a
                        href={`tel:${(items as InventoryItem[])[0].supplierContact}`}
                        className="ml-2 text-primary-600 hover:text-primary-800 underline"
                      >
                        Call to order
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Weeks-of-cover alert: stock may sit above par and still run out at the
          current wholesale pace. Cover = stock ÷ last 4 weeks of confirmed
          wholesale orders, matched by product name/SKU — unmatched items simply
          don't appear here. */}
      {(() => {
        const coverAlerts = items.filter((i: InventoryItem) => i.weeksOfCover != null && i.weeksOfCover < 2 && (Number(i.currentQuantity) || 0) > 0);
        if (coverAlerts.length === 0) return null;
        return (
          <div className="card p-4 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {coverAlerts.length} item{coverAlerts.length === 1 ? ' has' : 's have'} under two weeks of cover at the current wholesale pace
                </p>
                <p className="text-sm text-amber-700 mt-0.5">
                  {coverAlerts
                    .slice()
                    .sort((a: InventoryItem, b: InventoryItem) => (a.weeksOfCover ?? 99) - (b.weeksOfCover ?? 99))
                    .map((i: InventoryItem) => `${i.name} (${i.weeksOfCover} wk)`)
                    .join(', ')}
                </p>
                <p className="text-xs text-amber-600/80 mt-1">Cover = current stock ÷ the last 4 weeks of confirmed wholesale orders, matched by product name or SKU.</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Tracking coverage: menu items whose sales never touch inventory */}
      {(() => {
        const missing = (suggestions.recipeIssues || []).filter((i: any) => i.type === 'missing_recipe');
        if (missing.length === 0) return null;
        return (
          <div className="card p-4 bg-amber-50 border-amber-200">
            <div className="flex items-start gap-3">
              <UtensilsCrossed className="w-5 h-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-amber-800">
                  {missing.length} menu item{missing.length > 1 ? 's' : ''} sell{missing.length > 1 ? '' : 's'} without touching inventory
                </p>
                <p className="text-sm text-amber-700 mt-0.5">
                  These have no recipe linked, so sales don't deduct stock — counts drift and food cost can't see them:{' '}
                  <span className="font-medium">{missing.slice(0, 4).map((i: any) => i.menuItemName).join(', ')}{missing.length > 4 ? ` +${missing.length - 4} more` : ''}</span>
                </p>
                <a href="/recipes" className="mt-1.5 inline-block text-sm font-medium text-amber-800 underline hover:text-amber-900">
                  Link recipes in Recipes →
                </a>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Menu-Based Suggestions */}
      {(suggestions.suggestedIngredients?.length > 0 || suggestions.recipeIssues?.length > 0) && (
        <div className="card overflow-hidden">
          <button
            onClick={() => setShowSuggestions(!showSuggestions)}
            className="w-full p-4 flex items-center justify-between bg-slate-50 border-b hover:bg-slate-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg shadow-sm">
                <Lightbulb className="w-5 h-5 text-amber-500" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-800">Smart Suggestions from Menu</p>
                <p className="text-sm text-slate-600">
                  {suggestions.suggestedIngredients?.length || 0} suggested items based on your menu
                </p>
              </div>
            </div>
            {showSuggestions ? (
              <ChevronUp className="w-5 h-5 text-slate-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-slate-400" />
            )}
          </button>

          {showSuggestions && (
            <div className="p-4 space-y-4">
              {/* Recipe Issues */}
              {suggestions.recipeIssues?.filter((i: any) => i.type === 'missing_recipe').length > 0 && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <UtensilsCrossed className="w-4 h-4 text-slate-500" />
                    <p className="text-sm font-medium text-slate-700">Menu Items Without Recipes</p>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">
                    Add recipes to track ingredient costs and enable auto-deduction
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {suggestions.recipeIssues
                      .filter((i: any) => i.type === 'missing_recipe')
                      .slice(0, 5)
                      .map((issue: any, idx: number) => (
                        <span
                          key={idx}
                          className="px-2 py-1 bg-white rounded text-xs text-slate-600 border"
                        >
                          {issue.menuItemName}
                        </span>
                      ))}
                    {suggestions.recipeIssues.filter((i: any) => i.type === 'missing_recipe').length > 5 && (
                      <span className="px-2 py-1 text-xs text-slate-500">
                        +{suggestions.recipeIssues.filter((i: any) => i.type === 'missing_recipe').length - 5} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Suggested Ingredients */}
              {suggestions.suggestedIngredients?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-amber-500" />
                    <p className="text-sm font-medium text-slate-700">Suggested Inventory Items</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {suggestions.suggestedIngredients.slice(0, 9).map((suggestion: any, idx: number) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-800 text-sm truncate">{suggestion.name}</p>
                          <p className="text-xs text-slate-500 truncate">{suggestion.reason}</p>
                        </div>
                        <button
                          onClick={() => {
                            setFormData({
                              ...defaultFormData,
                              name: suggestion.name,
                              category: suggestion.category || 'miscellaneous',
                            });
                            setShowAddModal(true);
                          }}
                          className="ml-2 p-1.5 bg-primary-100 text-primary-700 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary-200"
                          title="Add to inventory"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  {suggestions.suggestedIngredients.length > 9 && (
                    <p className="text-xs text-slate-500 mt-2 text-center">
                      + {suggestions.suggestedIngredients.length - 9} more suggestions
                    </p>
                  )}
                </div>
              )}

              {/* Empty State */}
              {suggestions.suggestedIngredients?.length === 0 && suggestions.recipeIssues?.length === 0 && (
                <div className="text-center py-4">
                  <Check className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                  <p className="text-slate-600 font-medium">All set!</p>
                  <p className="text-sm text-slate-500">Your inventory is well-aligned with your menu</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search inventory..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full text-base"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="input w-full sm:w-auto sm:min-w-[160px] text-base py-3 sm:py-2"
          >
            <option value="all">All Categories</option>
            {INVENTORY_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={clsx(
                'flex-1 sm:flex-none px-4 py-3 sm:py-2 rounded-lg font-medium transition-colors text-sm',
                filter === 'all' ? 'bg-slate-800 text-white' : 'bg-white text-slate-600 active:bg-slate-100'
              )}
            >
              All Items
            </button>
            <button
              onClick={() => setFilter('low')}
              className={clsx(
                'flex-1 sm:flex-none px-4 py-3 sm:py-2 rounded-lg font-medium transition-colors text-sm',
                filter === 'low' ? 'bg-warning-600 text-white' : 'bg-white text-slate-600 active:bg-slate-100'
              )}
            >
              Low Stock
            </button>
          </div>
        </div>
      </div>

      {/* Inventory List */}
      {isLoading ? (
        <div className="text-center py-12">Loading...</div>
      ) : (
        <>
          {(pendingDeliveries || []).length > 0 && (
            <div className="card p-5">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Pending deliveries</p>
              <div className="space-y-1.5">
                {(pendingDeliveries || []).map((po: any) => (
                  <div key={po.id} className="flex items-baseline justify-between gap-2 text-sm cursor-pointer hover:bg-slate-50 -mx-2 px-2 py-0.5 rounded" onClick={() => { window.location.href = '/receiving'; }}>
                    <span className="text-slate-700 truncate">{po.vendorName} <span className="text-slate-400 font-mono text-xs">{po.poNumber}</span></span>
                    <span className="text-slate-500 whitespace-nowrap">{po.lines} line{po.lines === 1 ? '' : 's'} · ${((po.totalCents || 0) / 100).toLocaleString('en-US')}{po.expectedDelivery ? ` · expected ${new Date(po.expectedDelivery).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-400 mt-2">When the truck lands, click through to receive it — enter the invoice number from the slip plus any damaged or missing quantities, and that becomes the reconciliation.</p>
            </div>
          )}

          {depletion?.enabled && (
            <div className="card px-5 py-3.5 flex items-center justify-between gap-3 bg-emerald-50/60 border-emerald-100">
              <p className="text-sm text-emerald-900">
                <span className="font-medium">On hand is live.</span> Today's sales from Toast deplete ingredients through recipes automatically.
              </p>
              {depletion.lastAppliedAt && (
                <p className="text-xs text-emerald-700 whitespace-nowrap">
                  Updated {new Date(depletion.lastAppliedAt).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
            </div>
          )}

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredItems.map((item: InventoryItem) => {
              const status = getStockStatus(item);
              return (
                <div key={item.id} className="card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-slate-900 truncate">{item.name}</p>
                        <span className={clsx(
                          'badge text-xs flex-shrink-0',
                          status === 'critical' && 'badge-danger',
                          status === 'low' && 'badge-warning',
                          status === 'good' && 'badge-success'
                        )}>
                          {status === 'critical' ? 'Critical' : status === 'low' ? 'Low' : 'Good'}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mb-2">{item.category || 'Miscellaneous'}</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                        <div>
                          <span className="text-slate-400">Stock:</span>{' '}
                          <span className="font-medium">{item.currentQuantity} {item.unit}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Par:</span>{' '}
                          <span className="font-medium">{item.parLevel || 0} {item.unit}</span>
                        </div>
                        <div>
                          <span className="text-slate-400">Cost:</span>{' '}
                          <span className="font-medium">{formatCurrency(Math.round((item.unitCost || 0) * 100))}</span>
                        </div>
                        {item.weeksOfCover != null && (
                          <div title={`~${item.weeklyDepletion} ${item.unit}/week over the last 4 weeks of wholesale orders`}>
                            <span className="text-slate-400">Cover:</span>{' '}
                            <span className={clsx('font-medium', item.weeksOfCover < 2 && 'text-amber-700')}>{item.weeksOfCover} wk</span>
                          </div>
                        )}
                        {item.supplier && (
                          <div>
                            <span className="text-slate-400">Supplier:</span>{' '}
                            <span className="font-medium truncate">{item.supplier}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => openAdjustModal(item)}
                        className="p-3 rounded-xl bg-slate-100 active:bg-slate-200 transition-colors"
                        title="Adjust quantity"
                      >
                        <ArrowUp className="w-5 h-5 text-slate-600" />
                      </button>
                      <button
                        onClick={() => openEditModal(item)}
                        className="p-3 rounded-xl bg-slate-100 active:bg-slate-200 transition-colors"
                        title="Edit item"
                      >
                        <Edit2 className="w-5 h-5 text-slate-600" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block card overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="table-header">Item</th>
                  <th className="table-header">Category</th>
                  <th className="table-header">Current Stock</th>
                  {depletion?.enabled && <th className="table-header">Used today</th>}
                  <th className="table-header" title="Current stock ÷ the last 4 weeks of confirmed wholesale orders (matched by name/SKU). A dash means no recent wholesale depletion matched this item.">Cover</th>
                  <th className="table-header">Par Level</th>
                  <th className="table-header">Unit Cost</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredItems.map((item: InventoryItem) => {
                  const status = getStockStatus(item);
                  return (
                    <tr key={item.id} className="hover:bg-slate-50">
                      <td className="table-cell">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          {item.sku && <p className="text-sm text-slate-500">SKU: {item.sku}</p>}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="text-sm text-slate-600">{item.category || 'Miscellaneous'}</span>
                      </td>
                      <td className="table-cell">
                        {item.currentQuantity} {item.unit}
                      </td>
                      {depletion?.enabled && (
                        <td className="table-cell">
                          {usedToday[item.id]
                            ? <span className="text-slate-600 tabular-nums">−{usedToday[item.id]} {item.unit}</span>
                            : <span className="text-slate-300">—</span>}
                        </td>
                      )}
                      <td className="table-cell">
                        {item.weeksOfCover != null ? (
                          <span className={clsx('tabular-nums', item.weeksOfCover < 2 ? 'text-amber-700 font-medium' : 'text-slate-600')}
                            title={`~${item.weeklyDepletion} ${item.unit}/week over the last 4 weeks of wholesale orders`}>
                            {item.weeksOfCover} wk
                          </span>
                        ) : <span className="text-slate-300" title="No recent wholesale depletion matched to this item">—</span>}
                      </td>
                      <td className="table-cell">
                        {item.parLevel || 0} {item.unit}
                      </td>
                      <td className="table-cell">{formatCurrency(Math.round((item.unitCost || 0) * 100))}</td>
                      <td className="table-cell">
                        {status !== 'good' && ((item as any).onOrderQuantity || 0) > 0 ? (
                          <span className="badge badge-info" title={`${(item as any).onOrderQuantity} ${item.unit} on an open order`}>
                            On order · +{(item as any).onOrderQuantity}
                          </span>
                        ) : (
                          <span className={clsx(
                            'badge',
                            status === 'critical' && 'badge-danger',
                            status === 'low' && 'badge-warning',
                            status === 'good' && 'badge-success'
                          )}>
                            {status === 'critical' ? 'Critical' : status === 'low' ? 'Low' : 'Good'}
                          </span>
                        )}
                      </td>
                      <td className="table-cell">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openAdjustModal(item)}
                            className="btn btn-sm btn-secondary"
                            title="Adjust quantity"
                          >
                            <ArrowUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openEditModal(item)}
                            className="btn btn-sm btn-ghost"
                            title="Edit item"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium text-slate-600">Nothing counted yet</p>
              <p className="mt-1 text-sm max-w-md mx-auto">
                Add your first item above, or paste a stock list to the assistant in chat
                and it will set up items, units, and par levels for you.
              </p>
            </div>
          )}
        </>
      )}

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-md max-h-[90vh] md:max-h-[85vh] overflow-y-auto p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{selectedItem ? 'Edit Item' : 'Add Inventory Item'}</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedItem(null);
                  setFormData(defaultFormData);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={(e) => {
              e.preventDefault();
              if (selectedItem) {
                updateMutation.mutate({ id: selectedItem.id, data: { ...formData, unitCost: parseFloat(String(formData.unitCost)) || 0 } });
              } else {
                createMutation.mutate(formData);
              }
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input
                    type="text"
                    className="input w-full"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                  <select
                    className="input w-full"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    required
                  >
                    {INVENTORY_CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">SKU</label>
                    <input
                      type="text"
                      className="input w-full"
                      value={formData.sku}
                      onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Unit *</label>
                    {/* Free text with suggestions — kitchens count in whatever
                        unit the case ships in, and a fixed list can't even
                        represent existing items (kg, each, case…). */}
                    <input
                      type="text"
                      className="input w-full"
                      list="inventory-unit-suggestions"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="kg, lb, each, case…"
                    />
                    <datalist id="inventory-unit-suggestions">
                      {['each', 'kg', 'g', 'lb', 'oz', 'liter', 'ml', 'gal', 'case', 'dozen', 'bunch', 'sheet'].map((u) => (
                        <option key={u} value={u} />
                      ))}
                    </datalist>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Current Quantity</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={formData.currentQuantity}
                      onChange={(e) => setFormData({ ...formData, currentQuantity: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Par Level</label>
                    <input
                      type="number"
                      className="input w-full"
                      value={formData.parLevel}
                      onChange={(e) => setFormData({ ...formData, parLevel: parseFloat(e.target.value) || 0 })}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Unit Cost ($)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="input w-full"
                    value={String(formData.unitCost)}
                    onChange={(e) => setFormData({ ...formData, unitCost: e.target.value })}
                  />
                </div>
                <div className="border-t pt-4 mt-2">
                  <p className="text-sm font-medium text-slate-700 mb-3">Supplier Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Supplier Name</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., Sysco, US Foods"
                        value={formData.supplier}
                        onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Contact / Phone</label>
                      <input
                        type="text"
                        className="input w-full"
                        placeholder="e.g., (555) 123-4567"
                        value={formData.supplierContact}
                        onChange={(e) => setFormData({ ...formData, supplierContact: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedItem(null);
                    setFormData(defaultFormData);
                    setFormError('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isPending || updateMutation.isPending}
                >
                  {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (selectedItem ? 'Save Changes' : 'Add Item')}
                </button>
              </div>
            </form>

            {/* Price history — same timeline the costing engine writes */}
            {selectedItem && <UnitsSection key={selectedItem.id} itemId={selectedItem.id} unit={selectedItem.unit} />}
            {selectedItem && <CostHistorySection itemId={selectedItem.id} />}
          </div>
        </div>
      )}

      {/* Adjust Quantity Modal */}
      {showAdjustModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50">
          <div className="bg-white rounded-t-2xl md:rounded-xl shadow-xl w-full md:max-w-sm p-5 md:p-6 pb-safe">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Adjust Quantity</h2>
              <button
                onClick={() => {
                  setShowAdjustModal(false);
                  setSelectedItem(null);
                  setFormError('');
                }}
                className="p-2 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-slate-600 mb-4">
              {selectedItem.name} - Current: {selectedItem.currentQuantity} {selectedItem.unit}
            </p>
            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
                {formError}
              </div>
            )}
            <form onSubmit={(e) => {
              e.preventDefault();
              adjustMutation.mutate({
                id: selectedItem.id,
                quantity: adjustData.quantity,
                reason: adjustData.reason,
              });
            }}>
              <div className="space-y-4">
                {(() => {
                  const containers = (containersMap || {})[selectedItem.id] as Array<{ label: string; qty: number }> | undefined;
                  const counting = adjustData.reason === 'Inventory count' && containers && containers.length > 0;
                  if (!counting) {
                    return (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Adjustment (use negative to subtract)
                        </label>
                        <input
                          type="number"
                          className="input w-full"
                          value={adjustData.quantity}
                          onChange={(e) => setAdjustData({ ...adjustData, quantity: parseFloat(e.target.value) || 0 })}
                          required
                        />
                      </div>
                    );
                  }
                  const counted = countedFrom(containers, countRows, looseQty);
                  const current = Number(selectedItem.currentQuantity) || 0;
                  const delta = Math.round((counted - current) * 1000) / 1000;
                  return (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-slate-700">Count what you see — it converts itself:</p>
                      {containers.map((c) => (
                        <div key={c.label} className="flex items-center gap-2">
                          <span className="text-sm text-slate-600 flex-1">{c.label}</span>
                          <input
                            type="number" min="0" step="1"
                            className="input w-24 text-right"
                            value={countRows[c.label] ?? ''}
                            placeholder="0"
                            onChange={(e) => {
                              const v = parseFloat(e.target.value) || 0;
                              const next = { ...countRows, [c.label]: v };
                              setCountRows(next);
                              setAdjustData((a) => ({ ...a, quantity: Math.round((countedFrom(containers, next, looseQty) - current) * 1000) / 1000 }));
                            }}
                          />
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-slate-600 flex-1">loose {selectedItem.unit}</span>
                        <input
                          type="number" min="0" step="0.1"
                          className="input w-24 text-right"
                          value={looseQty || ''}
                          placeholder="0"
                          onChange={(e) => {
                            const v = parseFloat(e.target.value) || 0;
                            setLooseQty(v);
                            setAdjustData((a) => ({ ...a, quantity: Math.round((countedFrom(containers, countRows, v) - current) * 1000) / 1000 }));
                          }}
                        />
                      </div>
                      <div className="rounded-lg bg-slate-50 px-3 py-2 text-sm">
                        Counted: <span className="font-semibold tabular-nums">{Math.round(counted * 100) / 100} {selectedItem.unit}</span>
                        <span className="text-slate-400"> · system believed {current} → adjustment {delta >= 0 ? '+' : ''}{delta}</span>
                      </div>
                    </div>
                  );
                })()}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Reason *</label>
                  <select
                    className="input w-full"
                    value={adjustData.reason}
                    onChange={(e) => setAdjustData({ ...adjustData, reason: e.target.value })}
                    required
                  >
                    <option value="">Select reason...</option>
                    <option value="Received shipment">Received shipment</option>
                    <option value="Inventory count">Inventory count</option>
                    <option value="Waste/spoilage">Waste/spoilage</option>
                    <option value="Transfer">Transfer</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowAdjustModal(false);
                    setSelectedItem(null);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={adjustMutation.isPending}
                >
                  {adjustMutation.isPending ? 'Adjusting...' : 'Adjust'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showScanModal && (
        <InvoiceScanReceiveModal
          items={(inventoryData || []).map((it: any) => ({
            id: it.id,
            name: it.name,
            unitOfMeasure: it.unitOfMeasure,
          }))}
          onClose={() => setShowScanModal(false)}
        />
      )}
    </div>
  );
}


// Price timeline for an inventory item (from inventory_cost_history — the same
// records the costing engine writes on every receive/invoice/manual change).
function CostHistorySection({ itemId }: { itemId: string }) {
  const { data } = useQuery({
    queryKey: ['inventory-cost-history', itemId],
    queryFn: async () => {
      const res = await inventoryApi.getCostHistory(itemId);
      return (res.data?.data || res.data) as {
        history: Array<{ unitCostCents: number; previousCostCents: number | null; percentChange: number | null; source: string; changedAt: string }>;
      };
    },
    staleTime: 60 * 1000,
  });
  const history = data?.history || [];
  if (history.length === 0) return null;
  const sourceLabel: Record<string, string> = {
    receiving: 'Delivery received',
    invoice: 'Invoice scanned',
    manual: 'Edited by hand',
    initial: 'Item created',
  };
  return (
    <div className="mt-5 pt-4 border-t border-slate-200">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">Price history</h3>
      <div className="space-y-1.5 max-h-40 overflow-y-auto">
        {history.map((h, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-slate-500">
              {new Date(h.changedAt).toLocaleDateString()} · {sourceLabel[h.source] || h.source}
            </span>
            <span className="font-medium text-slate-800">
              ${(h.unitCostCents / 100).toFixed(2)}
              {h.percentChange != null && (
                <span className={h.percentChange > 0 ? 'text-red-600 ml-1.5 text-xs' : 'text-emerald-600 ml-1.5 text-xs'}>
                  {h.percentChange > 0 ? '+' : ''}{h.percentChange}%
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


// How this item is counted and ordered — editable in place, so unit
// standardization lives with the people who receive the cases.
function UnitsSection({ itemId, unit }: { itemId: string; unit: string }) {
  const qc = useQueryClient();
  const { data: containersMap } = useQuery({
    queryKey: ['count-containers'],
    queryFn: async () => (await api.get('/v1/inventory/count-containers')).data?.data || {},
  });
  const [rows, setRows] = useState<Array<{ label: string; qty: number }> | null>(null);
  const containers: Array<{ label: string; qty: number }> =
    rows ?? ((containersMap || {})[itemId] as any[] | undefined) ?? [];
  const saveContainers = useMutation({
    mutationFn: (next: Array<{ label: string; qty: number }>) =>
      api.put('/v1/inventory/count-containers', { itemId, containers: next.filter((c) => c.label.trim() && c.qty > 0) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['count-containers'] }),
  });

  const { data: packs } = useQuery({
    queryKey: ['item-suppliers', itemId],
    queryFn: async () => {
      const d = (await api.get(`/v1/inventory/${itemId}/suppliers`)).data?.data;
      const list = Array.isArray(d) ? d : d?.suppliers || [];
      return list.map((x: any) => ({ ...x, id: x.id || x.vendorItemId }));
    },
  });
  const [packEdits, setPackEdits] = useState<Record<string, { orderUnit: string; packSize: number; price: number }>>({});
  const savePack = useMutation({
    mutationFn: ({ id, orderUnit, packSize, price }: { id: string; orderUnit: string; packSize: number; price: number }) =>
      api.patch(`/v1/inventory/catalog/${id}`, { orderUnit, packSize, unitPriceCents: Math.round(price * 100) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['item-suppliers', itemId] });
      qc.invalidateQueries({ queryKey: ['order-suggestions'] });
    },
  });

  return (
    <div className="mt-6 border-t pt-4 space-y-5">
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-slate-700">Counted as</p>
          <button
            type="button"
            onClick={() => setRows([...(containers || []), { label: '', qty: 1 }])}
            className="btn btn-sm btn-ghost"
          >
            + container
          </button>
        </div>
        {containers.length === 0 && (
          <p className="text-xs text-slate-400 mb-1">No containers yet — counts ask for loose {unit}. Add how it physically arrives (case, bin, cup…).</p>
        )}
        <div className="space-y-1.5">
          {containers.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                className="input input-sm flex-1"
                placeholder={'e.g. case \u00b7 36\u00d71 lb'}
                value={c.label}
                onChange={(e) => setRows(containers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              />
              <span className="text-xs text-slate-400">=</span>
              <input
                type="number" min="0" step="0.01"
                className="input input-sm w-24 text-right"
                value={c.qty}
                onChange={(e) => setRows(containers.map((x, j) => (j === i ? { ...x, qty: parseFloat(e.target.value) || 0 } : x)))}
              />
              <span className="text-xs text-slate-500 w-10">{unit}</span>
              <button type="button" className="text-slate-300 hover:text-danger-500" onClick={() => setRows(containers.filter((_, j) => j !== i))}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        {rows !== null && (
          <button
            type="button"
            onClick={() => saveContainers.mutate(containers)}
            disabled={saveContainers.isPending}
            className="btn btn-sm btn-secondary mt-2"
          >
            {saveContainers.isPending ? 'Saving…' : saveContainers.isSuccess ? 'Saved ✓' : 'Save containers'}
          </button>
        )}
      </div>

      <div>
        <p className="text-sm font-medium text-slate-700 mb-2">Ordered as</p>
        {(!packs || packs.length === 0) ? (
          <p className="text-xs text-slate-400">No supplier catalog rows yet — add this item to a supplier in Ordering → Suppliers and its purchase unit becomes editable here.</p>
        ) : (
          <div className="space-y-1.5">
            {packs.map((pk: any) => {
              const e = packEdits[pk.id] || { orderUnit: pk.orderUnit || 'case', packSize: pk.packSize || 1, price: (pk.unitPriceCents || 0) / 100 };
              const dirty = e.orderUnit !== (pk.orderUnit || 'case') || e.packSize !== (pk.packSize || 1) || Math.round(e.price * 100) !== (pk.unitPriceCents || 0);
              return (
                <div key={pk.id} className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-slate-600 w-40 truncate">{pk.vendorName}{pk.vendorSku ? ` \u00b7 ${pk.vendorSku}` : ''}</span>
                  <input
                    className="input input-sm w-20"
                    value={e.orderUnit}
                    onChange={(ev) => setPackEdits((p) => ({ ...p, [pk.id]: { ...e, orderUnit: ev.target.value } }))}
                  />
                  <span className="text-xs text-slate-400">of</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="input input-sm w-20 text-right"
                    value={e.packSize}
                    onChange={(ev) => setPackEdits((p) => ({ ...p, [pk.id]: { ...e, packSize: parseFloat(ev.target.value) || 0 } }))}
                  />
                  <span className="text-xs text-slate-500">{unit}</span>
                  <span className="text-xs text-slate-400">@ $</span>
                  <input
                    type="number" min="0" step="0.01"
                    className="input input-sm w-24 text-right"
                    value={e.price}
                    onChange={(ev) => setPackEdits((p) => ({ ...p, [pk.id]: { ...e, price: parseFloat(ev.target.value) || 0 } }))}
                  />
                  {dirty && (
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary"
                      disabled={savePack.isPending}
                      onClick={() => savePack.mutate({ id: pk.id, ...e })}
                    >
                      Save
                    </button>
                  )}
                </div>
              );
            })}
            <p className="text-xs text-slate-400 pt-1">Ordering suggests whole {packs[0]?.orderUnit || 'case'}s using these packs; invoices and receiving read the same way.</p>
          </div>
        )}
      </div>
    </div>
  );
}
